import { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import i18n from "./i18n/i18n";
import { isEnglishPath } from "./utils/languageRouting";

import HomePage from "./pages/HomePage/HomePage";
import GetStartedPage from "./pages/GetStartedPage/GetStartedPage";
import AboutUs from "./pages/AboutUs/AboutUs";
import OutOfHome from "./pages/OutOfHome/OutOfHome";
import ServicePage from "./pages/ServicePage/ServicePage";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";

// i18n.js sets the *initial* language from the URL at load time (so the
// very first render/prerender pass is already correct), but doesn't
// react to subsequent client-side navigation between the FR and EN
// prefixes (e.g. clicking the EN/FR button, or a nav link, doesn't
// reload the page). This keeps i18next's active language following the
// URL for the lifetime of the SPA session.
function LanguageSync() {
  const location = useLocation();
  useEffect(() => {
    const lng = isEnglishPath(location.pathname) ? "en" : "fr";
    if (i18n.language !== lng) i18n.changeLanguage(lng);
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <LanguageSync />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/out-of-home" element={<OutOfHome />} />
          {/* City landing pages: same shared OutOfHome page/content,
              parameterized per city. Intentionally not linked from the
              main nav - reachable only via the /out-of-home FAQ and each
              page's own back link, per request. */}
          <Route
            path="/affichage-numerique-brossard"
            element={<OutOfHome cityKey="brossard" />}
          />
          <Route
            path="/affichage-numerique-chateauguay"
            element={<OutOfHome cityKey="chateauguay" />}
          />
          <Route
            path="/affichage-numerique-valleyfield"
            element={<OutOfHome cityKey="valleyfield" />}
          />
          <Route
            path="/affichage-numerique-beauharnois"
            element={<OutOfHome cityKey="beauharnois" />}
          />

          {/* Service landing pages: keyword-focused, thin pages (own
              hero/intro, shared FAQ + final CTA). FR-only for now. */}
          <Route
            path="/affichage-numerique"
            element={<ServicePage pageKey="affichageNumerique" />}
          />
          <Route
            path="/publicite-interieure"
            element={<ServicePage pageKey="publiciteInterieure" />}
          />
          <Route
            path="/compagnie-media"
            element={<ServicePage pageKey="compagnieMedia" />}
          />

          {/* EN mirrors of the pages above - see src/utils/languageRouting.js
              for the FR<->EN path convention this all depends on. */}
          <Route path="/en" element={<HomePage />} />
          <Route path="/en/get-started" element={<GetStartedPage />} />
          <Route path="/en/about" element={<AboutUs />} />
          <Route path="/en/out-of-home" element={<OutOfHome />} />
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;
